if (!window.__productCardInteractionsInit) {
  window.__productCardInteractionsInit = true;

  const PRODUCT_CARD_SELECTOR = 'product-card';
  const MEDIA_CLICK_SUPPRESSION_MS = 400;
  const initializedCards = new WeakSet();

  function getEventTargetElement(event) {
    if (event.target instanceof Element) return event.target;

    if (typeof event.composedPath !== 'function') return null;

    return event.composedPath().find((node) => node instanceof Element) ?? null;
  }

  function closestWithin(event, selector, container) {
    const target = getEventTargetElement(event);
    if (!target) return null;

    const match = target.closest(selector);
    return match && container.contains(match) ? match : null;
  }

  function containsNode(container, target) {
    return target instanceof Node && container.contains(target);
  }

  function suppressNextMediaClick(productCard) {
    productCard._suppressMediaClickUntil = Date.now() + MEDIA_CLICK_SUPPRESSION_MS;
  }

  function shouldSuppressMediaClick(productCard, event) {
    const suppressUntil = productCard._suppressMediaClickUntil ?? 0;

    if (suppressUntil < Date.now()) {
      delete productCard._suppressMediaClickUntil;
      return false;
    }

    if (!closestWithin(event, '.product-card__media', productCard)) {
      return false;
    }

    delete productCard._suppressMediaClickUntil;
    return true;
  }

  function showSlide(figure, index, updateIndex) {
    const dots = figure.querySelector('.product-card__dots');
    const media = figure.querySelector('.product-card__media');
    if (!dots || !media) return;

    const primary = media.querySelector('.product-card__image--primary');
    if (!primary) return;

    const slides = Array.from(media.querySelectorAll('.product-card__slide:not(.product-card__image--primary)'));

    if (updateIndex) {
      Array.from(dots.children).forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
      figure.dataset.carouselIndex = String(index);
    }

    if (index === 0) {
      primary.classList.remove('is-hidden');
      slides.forEach((slide) => slide.classList.remove('is-active'));
      return;
    }

    primary.classList.add('is-hidden');
    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index - 1);
    });
  }

  function rebuildDots(figure, mediaEl) {
    const dots = figure.querySelector('.product-card__dots');
    if (!dots) return;

    const imgCount = mediaEl?.querySelectorAll('img').length ?? 0;
    dots.innerHTML = Array.from(
      { length: imgCount },
      (_, index) => `<span class="product-card__dot${index === 0 ? ' is-active' : ''}"></span>`,
    ).join('');
    figure.dataset.carouselIndex = '0';
  }

  function swapCardInfo(productCard, figure, colorDot) {
    const mediaEl = figure.querySelector('.product-card__media');
    const info = productCard.querySelector('.product-card__info');
    const titleEl = info?.querySelector('.product-title');
    const priceListEl = info?.querySelector('price-list');
    const optionsEl = info?.querySelector('.product-card__options');
    const tmpl = colorDot.querySelector('template');

    if (!figure._origState) {
      figure._origState = {
        mediaHref: mediaEl?.href,
        titleText: titleEl?.textContent,
        titleHref: titleEl?.href,
        priceHtml: priceListEl?.innerHTML,
        optionsOuterHtml: optionsEl?.outerHTML ?? null,
        mediaNodes: Array.from(mediaEl?.childNodes ?? []),
      };
      figure._origState.mediaNodes.forEach((node) => node.remove());
    } else if (mediaEl) {
      mediaEl.innerHTML = '';
    }

    if (!tmpl) {
      const originalState = figure._origState;
      if (!originalState) return;

      originalState.mediaNodes.forEach((node) => mediaEl?.appendChild(node));

      if (mediaEl) mediaEl.href = originalState.mediaHref;

      if (titleEl) {
        titleEl.textContent = originalState.titleText;
        titleEl.href = originalState.titleHref;
      }

      if (priceListEl) priceListEl.innerHTML = originalState.priceHtml;

      if (originalState.optionsOuterHtml) {
        if (optionsEl) optionsEl.outerHTML = originalState.optionsOuterHtml;
        else info?.insertAdjacentHTML('beforeend', originalState.optionsOuterHtml);
      }

      rebuildDots(figure, mediaEl);
      delete figure._origState;
      delete figure._activeColorDot;
      return;
    }

    const frag = tmpl.content.cloneNode(true);
    const imgs = frag.querySelectorAll('img');
    if (imgs.length > 0) {
      imgs[0].classList.add('product-card__image--primary');
      imgs[0].classList.remove('product-card__slide', 'is-hidden');
    }
    mediaEl?.appendChild(frag);

    rebuildDots(figure, mediaEl);

    const url = colorDot.dataset.url;
    if (mediaEl && url) mediaEl.href = url;

    if (titleEl) {
      if (colorDot.dataset.title) titleEl.textContent = colorDot.dataset.title;
      if (url) titleEl.href = url;
    }

    if (priceListEl && colorDot.dataset.price) {
      if (
        colorDot.dataset.comparePrice &&
        parseInt(colorDot.dataset.comparePrice, 10) > parseInt(colorDot.dataset.price, 10)
      ) {
        priceListEl.innerHTML = `<price-list class="price-list "><sale-price class="Body-3-f text-black h6 text-on-sale">
        <span class="sr-only">Sale price</span>${colorDot.dataset.priceMoney}</sale-price><compare-at-price class="Body-3-f text-black h6 text-subdued line-through">
        <span class="sr-only">Regular price</span>${colorDot.dataset.comparePriceMoney}</compare-at-price></price-list>`;
      } else {
        priceListEl.innerHTML = `<price-list class="price-list"><sale-price class="Body-3-f ph6 text-subdued text-black">${colorDot.dataset.priceMoney}</sale-price></price-list>`;
      }
    }

    const tmplOptions = tmpl.content.querySelector('.product-card__options');
    if (tmplOptions) {
      if (optionsEl) {
        optionsEl.innerHTML = tmplOptions.innerHTML;
      } else if (info) {
        info.insertAdjacentHTML('beforeend', tmplOptions.outerHTML);
      }
    } else if (optionsEl) {
      optionsEl.remove();
    }
  }

  function animateToSlide(figure, media, fromIndex, toIndex) {
    const primary = media.querySelector('.product-card__image--primary');
    if (!primary) return;

    const secondarySlides = Array.from(
      media.querySelectorAll('.product-card__slide:not(.product-card__image--primary)'),
    );

    function slideEl(index) {
      return index === 0 ? primary : (secondarySlides[index - 1] ?? null);
    }

    const dir = toIndex > fromIndex ? 1 : -1;
    const mediaWidth = media.getBoundingClientRect().width;
    const duration = 200;
    const currentEl = slideEl(fromIndex);
    const adjacentEl = slideEl(toIndex);

    showSlide(figure, fromIndex, false);

    media.style.height = media.offsetHeight + 'px';

    if (currentEl) {
      currentEl.style.transition = 'none';
      currentEl.style.position = 'absolute';
      currentEl.style.inset = '0';
      currentEl.style.width = '100%';
      currentEl.style.height = '100%';
      currentEl.style.opacity = '1';
      currentEl.style.transform = 'translateX(0)';
      currentEl.style.zIndex = '2';
    }

    if (adjacentEl) {
      adjacentEl.style.transition = 'none';
      adjacentEl.style.position = 'absolute';
      adjacentEl.style.inset = '0';
      adjacentEl.style.width = '100%';
      adjacentEl.style.height = '100%';
      adjacentEl.style.opacity = '1';
      adjacentEl.style.transform = dir === 1 ? 'translateX(100%)' : 'translateX(-100%)';
      adjacentEl.style.zIndex = '1';
      void adjacentEl.offsetHeight;
    }

    const targetCurrent = dir === 1 ? -mediaWidth : mediaWidth;

    if (currentEl) {
      currentEl.style.transition = `transform ${duration}ms ease-out`;
      currentEl.style.transform = `translateX(${targetCurrent}px)`;
    }

    if (adjacentEl) {
      adjacentEl.style.transition = `transform ${duration}ms ease-out`;
      adjacentEl.style.transform = 'translateX(0)';
    }

    setTimeout(() => {
      if (currentEl) {
        currentEl.style.transition = 'none';
        currentEl.style.opacity = '0';
      }
      if (adjacentEl) {
        adjacentEl.style.transition = 'none';
      }

      showSlide(figure, toIndex, true);
      requestAnimationFrame(() => {
        [currentEl, adjacentEl].forEach((element) => {
          if (!element) return;
          element.style.cssText = '';
        });
        media.style.height = '';
      });
    }, duration);
  }

  function handleDotClick(productCard, event) {
    const dot = closestWithin(event, '.product-card__dot', productCard);
    if (!dot) return;

    const dots = dot.closest('.product-card__dots');
    if (!dots) return;

    const figure = dots.closest('.product-card__figure');
    if (!figure) return;

    const toIndex = Array.from(dots.children).indexOf(dot);
    const fromIndex = parseInt(figure.dataset.carouselIndex ?? '0', 10);

    if (toIndex === fromIndex) return;

    const media = figure.querySelector('.product-card__media');
    if (!media) return;

    animateToSlide(figure, media, fromIndex, toIndex);
  }

  function handleCardHover(productCard, event) {
    if (window.innerWidth > 1024) {
      const media = closestWithin(event, '.product-card__media', productCard);
      if (media && !containsNode(media, event.relatedTarget)) {
        const figure = media.closest('.product-card__figure');
        const dots = figure?.querySelector('.product-card__dots');
        if (figure && dots && dots.children.length > 1) {
          const savedIndex = parseInt(figure.dataset.carouselIndex ?? '0', 10);
          if (savedIndex === 0) {
            showSlide(figure, dots.children.length - 1, false);
          }
        }
      }
    }

    const colorDot = closestWithin(event, '.product-card__color', productCard);
    if (!colorDot || containsNode(colorDot, event.relatedTarget)) return;

    const colors = colorDot.closest('.product-card__colors');
    if (!colors || colors.children.length <= 1) return;

    const figure = colors.closest('.product-card__figure');
    if (!figure || figure._activeColorDot === colorDot) return;

    figure._activeColorDot = colorDot;
    swapCardInfo(productCard, figure, colorDot);
  }

  function handleFigureLeave(productCard, event) {
    const figure = closestWithin(event, '.product-card__figure', productCard);
    if (!figure || containsNode(figure, event.relatedTarget)) return;

    const savedIndex = parseInt(figure.dataset.carouselIndex ?? '0', 10);
    showSlide(figure, savedIndex, false);
  }

  function handleMediaPointerDown(productCard, event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const media = closestWithin(event, '.product-card__media', productCard);
    if (!media) return;

    const figure = media.closest('.product-card__figure');
    if (!figure) return;

    const dots = figure.querySelector('.product-card__dots');
    if (!dots || dots.children.length <= 1) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const mediaWidth = media.getBoundingClientRect().width;
    const total = dots.children.length;
    const current = parseInt(figure.dataset.carouselIndex ?? '0', 10);

    const primary = media.querySelector('.product-card__image--primary');
    if (!primary) return;

    const secondarySlides = Array.from(
      media.querySelectorAll('.product-card__slide:not(.product-card__image--primary)'),
    );

    function slideEl(index) {
      return index === 0 ? primary : (secondarySlides[index - 1] ?? null);
    }

    let dragging = false;
    let cancelled = false;
    let adjacentIdx = -1;
    let lastDir = 0;

    function resetDragState() {
      const currentEl = slideEl(current);
      const adjacentEl = slideEl(adjacentIdx);
      showSlide(figure, current, false);

      requestAnimationFrame(() => {
        [currentEl, adjacentEl].forEach((element) => {
          if (!element) return;
          element.style.cssText = '';
        });

        adjacentIdx = -1;
        lastDir = 0;
        restoreMedia();
      });
    }

    function restoreMedia() {
      media.style.height = '';
    }

    function cleanupDragListeners() {
      media.removeEventListener('pointermove', onMove);
      media.removeEventListener('pointerup', onUp);
      media.removeEventListener('pointercancel', onCancel);
    }

    function startDrag(dir) {
      media.style.height = media.offsetHeight + 'px';

      const nextIdx = dir === 1 ? current + 1 : current - 1;
      if (nextIdx < 0 || nextIdx >= total) {
        restoreMedia();
        return;
      }

      adjacentIdx = nextIdx;
      lastDir = dir;
      showSlide(figure, current, false);

      const currentEl = slideEl(current);
      const adjacentEl = slideEl(adjacentIdx);

      if (currentEl) {
        currentEl.style.transition = 'none';
        currentEl.style.position = 'absolute';
        currentEl.style.inset = '0';
        currentEl.style.width = '100%';
        currentEl.style.height = '100%';
        currentEl.style.opacity = '1';
        currentEl.style.transform = 'translateX(0)';
        currentEl.style.zIndex = '2';
      }

      if (adjacentEl) {
        adjacentEl.style.transition = 'none';
        adjacentEl.style.position = 'absolute';
        adjacentEl.style.inset = '0';
        adjacentEl.style.width = '100%';
        adjacentEl.style.height = '100%';
        adjacentEl.style.opacity = '1';
        adjacentEl.style.transform = `translateX(${dir === 1 ? mediaWidth : -mediaWidth}px)`;
        adjacentEl.style.zIndex = '1';
        void adjacentEl.offsetHeight;
      }
    }

    function moveStrip(dx) {
      const newDir = dx < 0 ? 1 : -1;
      if (newDir !== lastDir) {
        const nextIdx = newDir === 1 ? current + 1 : current - 1;
        endDrag(false, true);
        if (nextIdx < 0 || nextIdx >= total) return;
        startDrag(newDir);
      }

      if (adjacentIdx === -1) return;

      const currentEl = slideEl(current);
      const adjacentEl = slideEl(adjacentIdx);
      const adjacentStart = lastDir === 1 ? mediaWidth : -mediaWidth;

      if (currentEl) {
        currentEl.style.transition = 'none';
        currentEl.style.transform = `translateX(${dx}px)`;
      }

      if (adjacentEl) {
        adjacentEl.style.transition = 'none';
        adjacentEl.style.transform = `translateX(${adjacentStart + dx}px)`;
      }
    }

    function endDrag(commit, silent) {
      const currentEl = slideEl(current);
      const adjacentEl = slideEl(adjacentIdx);
      const adjacentStart = lastDir === 1 ? mediaWidth : -mediaWidth;
      const duration = 200;

      if (commit) {
        const targetCurrent = lastDir === 1 ? -mediaWidth : mediaWidth;

        if (currentEl) {
          currentEl.style.transition = `transform ${duration}ms ease-out`;
          currentEl.style.transform = `translateX(${targetCurrent}px)`;
        }

        if (adjacentEl) {
          adjacentEl.style.transition = `transform ${duration}ms ease-out`;
          adjacentEl.style.transform = 'translateX(0)';
        }

        setTimeout(() => {
          if (currentEl) {
            currentEl.style.transition = 'none';
            currentEl.style.opacity = '0';
          }

          if (adjacentEl) {
            adjacentEl.style.transition = 'none';
          }

          showSlide(figure, adjacentIdx, true);
          requestAnimationFrame(() => {
            [currentEl, adjacentEl].forEach((element) => {
              if (!element) return;
              element.style.cssText = '';
            });
            adjacentIdx = -1;
            lastDir = 0;
            restoreMedia();
          });
        }, duration);

        return;
      }

      if (silent) {
        resetDragState();
        return;
      }

      if (currentEl) {
        currentEl.style.transition = `transform ${duration}ms ease-out`;
        currentEl.style.transform = 'translateX(0)';
      }

      if (adjacentEl) {
        adjacentEl.style.transition = `transform ${duration}ms ease-out`;
        adjacentEl.style.transform = `translateX(${adjacentStart}px)`;
      }

      setTimeout(() => {
        if (currentEl) {
          currentEl.style.transition = 'none';
          currentEl.style.opacity = '1';
          currentEl.style.transform = 'translateX(0)';
        }

        if (adjacentEl) {
          adjacentEl.style.transition = 'none';
          adjacentEl.style.opacity = '0';
          adjacentEl.style.transform = `translateX(${adjacentStart}px)`;
        }

        showSlide(figure, current, false);

        requestAnimationFrame(() => {
          [currentEl, adjacentEl].forEach((element) => {
            if (!element) return;
            element.style.cssText = '';
          });

          adjacentIdx = -1;
          lastDir = 0;
          restoreMedia();
        });
      }, duration);
    }

    function onMove(moveEvent) {
      if (cancelled) return;

      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      if (!dragging) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;

        if (Math.abs(dy) > Math.abs(dx)) {
          cancelled = true;
          return;
        }

        dragging = true;
        suppressNextMediaClick(productCard);
        try {
          media.setPointerCapture(event.pointerId);
        } catch (_) {}
        startDrag(dx < 0 ? 1 : -1);
      }

      moveEvent.preventDefault();
      moveStrip(dx);
    }

    function onUp(upEvent) {
      cleanupDragListeners();
      if (!dragging || adjacentIdx === -1) return;

      const dx = upEvent.clientX - startX;
      endDrag(Math.abs(dx) > mediaWidth * 0.2, false);
    }

    function onCancel() {
      cleanupDragListeners();
      if (dragging && adjacentIdx !== -1) endDrag(false, false);
    }

    media.addEventListener('pointermove', onMove, { passive: false });
    media.addEventListener('pointerup', onUp);
    media.addEventListener('pointercancel', onCancel);
  }

  function onCardClick(event) {
    const productCard = event.currentTarget;
    if (!(productCard instanceof HTMLElement)) return;

    if (shouldSuppressMediaClick(productCard, event)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    handleDotClick(productCard, event);
  }

  function onCardPointerOver(event) {
    const productCard = event.currentTarget;
    if (!(productCard instanceof HTMLElement)) return;

    handleCardHover(productCard, event);
  }

  function onCardPointerOut(event) {
    const productCard = event.currentTarget;
    if (!(productCard instanceof HTMLElement)) return;

    handleFigureLeave(productCard, event);
  }

  function onCardPointerDown(event) {
    const productCard = event.currentTarget;
    if (!(productCard instanceof HTMLElement)) return;

    handleMediaPointerDown(productCard, event);
  }

  function initProductCard(productCard) {
    if (!(productCard instanceof HTMLElement) || initializedCards.has(productCard)) return;

    initializedCards.add(productCard);
    productCard.addEventListener('click', onCardClick);
    productCard.addEventListener('pointerover', onCardPointerOver);
    productCard.addEventListener('pointerout', onCardPointerOut);
    productCard.addEventListener('pointerdown', onCardPointerDown);
  }

  function initProductCards(root) {
    root.querySelectorAll(PRODUCT_CARD_SELECTOR).forEach((productCard) => {
      initProductCard(productCard);
    });
  }

  function initAddedNode(node) {
    if (!(node instanceof Element)) return;

    if (node.matches(PRODUCT_CARD_SELECTOR)) {
      initProductCard(node);
    }

    initProductCards(node);
  }

  initProductCards(document);

  if ('MutationObserver' in window) {
    window.__productCardInteractionsObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          initAddedNode(node);
        });
      });
    });

    window.__productCardInteractionsObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
}
